/**
 * Composite editor for the email sender registry (`emailSending`).
 *
 * Two halves, and the second is the one that matters. Editing a sender's
 * address is the visible, careful operation. **Re-pointing a message at a
 * different sender is two clicks**, and if the new sender is unmonitored it
 * silently takes away both the BCC and the customer's reply path — nothing
 * errors, and the only symptom is a reply nobody ever reads. So every binding
 * renders the sender it actually resolves to, and a message whose replies
 * matter shows a warning when that sender isn't monitored.
 *
 * Validation mirrors sm4rt-api's `_coerce_email_sending`, which is the
 * authority — this copy exists so an admin sees which row is wrong instead of
 * one rejection for the whole save. The rules live in `emailSendingHelpers`.
 */

import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SendIcon from '@mui/icons-material/Send';
import type { EmailSendingConfig } from '@techgitter/contracts';
import { EMAIL_MESSAGE_KEYS, VERIFIED_SENDING_DOMAINS } from '@techgitter/contracts';

import { Chip } from '../../components/chip';
import { sendSenderTestEmail } from '../../services/appSettingsApi';
import { COMPACT_FIELD_SX, SWITCH_ACCENT_SX } from './fieldStyles';
import {
  describeSender,
  fromSenderRows,
  removeSender,
  renameSender,
  seedEmailSending,
  toBindingRows,
  toSenderRows,
  validateSenderRows,
} from './emailSendingHelpers';

const SECTION_LABEL_SX = {
  fontSize: 10,
  fontWeight: 500,
  color: '#888',
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
};

const RESOLVED_SX = {
  fontSize: 11,
  color: '#666',
  fontFamily:
    'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
};

export interface EmailSendingEditorProps {
  value: EmailSendingConfig | null;
  onChange: (next: EmailSendingConfig | null) => void;
  disabled?: boolean;
  error?: string;
}

type TestState = { sender: string; status: 'sending' | 'ok' | 'error'; message?: string };

export const EmailSendingEditor: React.FC<EmailSendingEditorProps> = ({
  value,
  onChange,
  disabled,
  error,
}) => {
  const [test, setTest] = useState<TestState | null>(null);

  // Null = "use the emailer's built-in registry". Turning the override on
  // seeds from the same defaults the emailer ships, so an admin starts from a
  // working registry rather than an empty one.
  const enabled = value !== null;
  const config = value ?? seedEmailSending();

  const senderRows = useMemo(() => toSenderRows(config), [config]);
  const rowErrors = useMemo(() => validateSenderRows(senderRows), [senderRows]);
  const bindingRows = useMemo(
    () => toBindingRows(config, EMAIL_MESSAGE_KEYS),
    [config],
  );
  const senderNames = senderRows.map((r) => r.name).filter(Boolean);

  const update = (next: EmailSendingConfig) => onChange(next);

  const updateRow = (idx: number, patch: Partial<(typeof senderRows)[number]>) => {
    // A rename is not a field edit: it moves the map key, so every binding
    // pointing at the old name has to move with it or the save is rejected
    // naming a sender the admin can no longer see.
    if (patch.name !== undefined && patch.name !== senderRows[idx].name) {
      update(renameSender(config, senderRows[idx].name, patch.name));
      return;
    }
    const next = senderRows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    update(fromSenderRows(next, config.bindings, config.fallbackSender));
  };

  const addRow = () => {
    const next = [
      ...senderRows,
      { name: '', fromEmail: '', fromName: null, bcc: null, monitored: false },
    ];
    update(fromSenderRows(next, config.bindings, config.fallbackSender));
  };

  const runTest = async (sender: string) => {
    setTest({ sender, status: 'sending' });
    try {
      const result = await sendSenderTestEmail(sender);
      setTest({
        sender,
        status: 'ok',
        message: result.dryRun
          ? 'Accepted, but the service is in dry-run so nothing was delivered.'
          : `Sent as ${describeSender(result.sender ?? undefined)}`,
      });
    } catch (e) {
      setTest({
        sender,
        status: 'error',
        message: e instanceof Error ? e.message : 'Test failed',
      });
    }
  };

  return (
    <Stack spacing={2}>
      <FormControlLabel
        control={
          <Switch
            sx={SWITCH_ACCENT_SX}
            checked={enabled}
            onChange={(e) => onChange(e.target.checked ? seedEmailSending() : null)}
            disabled={disabled}
          />
        }
        label={
          enabled
            ? 'Custom sender registry'
            : 'Using the built-in senders (noreply, renewals, accounts, sales, status)'
        }
      />

      {error && (
        <Typography sx={{ fontSize: 12, color: '#b03a2e' }}>{error}</Typography>
      )}

      {enabled && (
        <>
          {/* ------------------------------------------------- senders ---- */}
          <Stack spacing={1}>
            <Typography sx={SECTION_LABEL_SX}>Senders</Typography>
            <Typography sx={{ fontSize: 12, color: '#666' }}>
              A From address must be on {VERIFIED_SENDING_DOMAINS.join(' or ')} —
              anything else is refused at send time. Mark a sender monitored when
              a person reads replies to it; that is what warns you below before a
              customer&apos;s reply goes somewhere nobody looks.
            </Typography>

            {senderRows.length === 0 && (
              <Typography sx={{ fontSize: 12, color: '#bbb', fontStyle: 'italic' }}>
                No senders — add at least one.
              </Typography>
            )}

            {senderRows.map((row, idx) => {
              const errs = rowErrors[idx] ?? {};
              const testing = test?.sender === row.name && test.status === 'sending';
              return (
                <Stack key={idx} direction="row" spacing={1} alignItems="flex-start">
                  <TextField
                    size="small"
                    label="Name"
                    value={row.name}
                    onChange={(e) => updateRow(idx, { name: e.target.value })}
                    disabled={disabled}
                    error={Boolean(errs.name)}
                    helperText={errs.name ?? ' '}
                    sx={{ ...COMPACT_FIELD_SX, width: 120 }}
                  />
                  <TextField
                    size="small"
                    label="From address"
                    value={row.fromEmail}
                    onChange={(e) => updateRow(idx, { fromEmail: e.target.value })}
                    disabled={disabled}
                    error={Boolean(errs.fromEmail)}
                    helperText={errs.fromEmail ?? ' '}
                    sx={{ ...COMPACT_FIELD_SX, width: 260 }}
                  />
                  <TextField
                    size="small"
                    label="Display name"
                    value={row.fromName ?? ''}
                    onChange={(e) =>
                      updateRow(idx, { fromName: e.target.value || null })
                    }
                    disabled={disabled}
                    helperText=" "
                    sx={{ ...COMPACT_FIELD_SX, width: 180 }}
                  />
                  <TextField
                    size="small"
                    label="BCC"
                    value={row.bcc ?? ''}
                    onChange={(e) => updateRow(idx, { bcc: e.target.value || null })}
                    disabled={disabled}
                    error={Boolean(errs.bcc)}
                    helperText={errs.bcc ?? ' '}
                    sx={{ ...COMPACT_FIELD_SX, width: 240 }}
                  />
                  <Tooltip title="A person reads replies to this address">
                    <FormControlLabel
                      sx={{ mt: '2px', mr: 0 }}
                      control={
                        <Switch
                          size="small"
                          sx={SWITCH_ACCENT_SX}
                          checked={row.monitored}
                          onChange={(e) =>
                            updateRow(idx, { monitored: e.target.checked })
                          }
                          disabled={disabled}
                        />
                      }
                      label={
                        <Typography sx={{ fontSize: 11, color: '#666' }}>
                          Monitored
                        </Typography>
                      }
                    />
                  </Tooltip>
                  <Tooltip title="Send a test email to yourself as this sender. Tests the saved configuration, so save first.">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ mt: '1px' }}
                        aria-label={`Send a test email as ${row.name || 'this sender'}`}
                        onClick={() => runTest(row.name)}
                        disabled={disabled || testing || !row.name.trim()}
                      >
                        {testing ? (
                          <CircularProgress size={14} />
                        ) : (
                          <SendIcon sx={{ fontSize: 16, color: '#4A90D9' }} />
                        )}
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Remove this sender, and any message bound to it">
                    <span>
                      <IconButton
                        size="small"
                        sx={{ mt: '1px' }}
                        aria-label={`Remove this sender${row.name ? ` (${row.name})` : ''}`}
                        onClick={() => update(removeSender(config, row.name))}
                        disabled={disabled}
                      >
                        <DeleteIcon sx={{ fontSize: 16, color: '#aaa' }} />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>
              );
            })}

            <Box>
              <Button
                size="small"
                startIcon={<AddIcon />}
                onClick={addRow}
                disabled={disabled}
                sx={{ fontSize: 12, textTransform: 'none' }}
              >
                Add sender
              </Button>
            </Box>

            {test && test.status !== 'sending' && (
              <Typography
                sx={{
                  fontSize: 12,
                  color: test.status === 'ok' ? '#0D7A56' : '#b03a2e',
                }}
              >
                {test.sender}: {test.message}
              </Typography>
            )}
          </Stack>

          {/* ------------------------------------------------ bindings ---- */}
          <Stack spacing={1}>
            <Typography sx={SECTION_LABEL_SX}>Which sender each email uses</Typography>

            {bindingRows.map((row) => (
              <Stack
                key={row.key}
                direction="row"
                spacing={1}
                alignItems="flex-start"
                sx={{
                  py: 0.75,
                  borderTop: '0.5px solid #e2eaf3',
                }}
              >
                <Box sx={{ width: 260, minWidth: 0 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Typography sx={{ fontSize: 13, color: '#1a1a1a' }}>
                      {row.label}
                    </Typography>
                    {row.inert && (
                      <Chip tone="neutral">Not sending yet</Chip>
                    )}
                    {row.unknown && <Chip tone="warning">Unknown key</Chip>}
                  </Stack>
                  <Typography sx={{ fontSize: 11, color: '#888' }}>
                    {row.description}
                  </Typography>
                </Box>

                <TextField
                  size="small"
                  select
                  label="Sender"
                  value={row.senderName}
                  onChange={(e) => {
                    const bindings = { ...config.bindings };
                    if (e.target.value) bindings[row.key] = e.target.value;
                    else delete bindings[row.key];
                    update({ ...config, bindings });
                  }}
                  disabled={disabled}
                  helperText=" "
                  sx={{ ...COMPACT_FIELD_SX, width: 150 }}
                >
                  <MenuItem value="">
                    <em>Fallback</em>
                  </MenuItem>
                  {senderNames.map((name) => (
                    <MenuItem key={name} value={name}>
                      {name}
                    </MenuItem>
                  ))}
                </TextField>

                <Box sx={{ flex: 1, minWidth: 0, pt: '4px' }}>
                  <Typography sx={RESOLVED_SX} noWrap title={describeSender(row.resolved)}>
                    {describeSender(row.resolved)}
                  </Typography>
                  {row.warning && (
                    <Typography sx={{ fontSize: 11, color: '#C07A10' }}>
                      ⚠ {row.warning}
                    </Typography>
                  )}
                </Box>
              </Stack>
            ))}
          </Stack>

          {/* ------------------------------------------------ fallback ---- */}
          <Stack spacing={0.5}>
            <Typography sx={SECTION_LABEL_SX}>Fallback</Typography>
            <Typography sx={{ fontSize: 12, color: '#666' }}>
              Used for a message this app sends with a key nothing above
              recognises. It still sends — an email that never goes out is worse
              than one from a slightly wrong address — so this should be an
              address you would not mind a stray message arriving from.
            </Typography>
            <TextField
              size="small"
              select
              label="Fallback sender"
              value={senderNames.includes(config.fallbackSender) ? config.fallbackSender : ''}
              onChange={(e) => update({ ...config, fallbackSender: e.target.value })}
              disabled={disabled}
              error={!senderNames.includes(config.fallbackSender)}
              helperText={
                senderNames.includes(config.fallbackSender)
                  ? ' '
                  : 'Pick a sender — the save is refused without one'
              }
              sx={{ ...COMPACT_FIELD_SX, width: 220 }}
            >
              {senderNames.map((name) => (
                <MenuItem key={name} value={name}>
                  {name}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </>
      )}
    </Stack>
  );
};
